import has from 'lodash/has';
import filter from 'lodash/filter';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import forEach from 'lodash/forEach';
import difference from 'lodash/difference';
import orderBy from 'lodash/orderBy';
import includes from 'lodash/includes';

import {
  GET_ALL_CREDENTIALS_SUCCESS,
  GET_ALL_CREDENTIALS_FAILURE,
  GET_CREDENTIALS_SUCCESS,
  GET_CREDENTIAL_SILENT_SUCCESS,
  GET_CREDENTIAL_SUCCESS,
  ADD_CREDENTIAL_SUCCESS,
  ADD_CREDENTIAL_FAILURE,
  PROBE_CREDENTIAL_REQUEST,
  PROBE_CREDENTIAL_SUCCESS,
  PROBE_CREDENTIAL_FAILURE,
  PROBE_CREDENTIAL_SILENT_REQUEST,
  PROBE_CREDENTIAL_SILENT_SUCCESS,
  PROBE_CREDENTIAL_SILENT_FAILURE,
  DELETE_CREDENTIAL_SUCCESS,
  LOGOUT_COMPLETE,
} from 'js/constants/actionTypes';

const initialState = {
  lastUpdated: null,
  listing: [], // paginated IDs
  byId: [], // all IDs received from the unpaginated GET /data_credentials call
  sortedIds: [],
  byType: {},
  error: null,
  // TODO: remove probeStatus, probingId, and all of their usages
  probeStatus: null,
  probingId: null,
};

export const credentialsReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_ALL_CREDENTIALS_SUCCESS:
      return saveCredentials(state, action);

    case GET_CREDENTIAL_SUCCESS:
    case GET_CREDENTIAL_SILENT_SUCCESS:
      return saveCredential(state, action);

    case GET_CREDENTIALS_SUCCESS:
      return saveCredentialListing(state, action);

    case GET_ALL_CREDENTIALS_FAILURE:
      return removeCredentials(state, action);

    case ADD_CREDENTIAL_SUCCESS:
      return addCredential(state, action);

    case PROBE_CREDENTIAL_REQUEST:
    case PROBE_CREDENTIAL_SILENT_REQUEST:
      return probeCredential(state, action);

    case PROBE_CREDENTIAL_SUCCESS:
    case PROBE_CREDENTIAL_FAILURE:
    case PROBE_CREDENTIAL_SILENT_SUCCESS:
    case PROBE_CREDENTIAL_SILENT_FAILURE:
      return updateCredentialStatus(state, action);

    case DELETE_CREDENTIAL_SUCCESS:
      return deleteCredential(state, action);

    case LOGOUT_COMPLETE:
      return initialState;

    case ADD_CREDENTIAL_FAILURE:
    default:
      return state;
  }
};

export default credentialsReducer;

function saveCredentials(state, action) {
  const {
    result,
    entities: { credentials },
  } = action;
  let credentialsByType = getCredentialsByType(state, action);
  let sortedEntities = orderBy(credentials, ['created_at'], ['desc']);
  let sortedIds = map(sortedEntities, 'id');

  //get data map credentials and remove them from sortedIds;
  let dataMapCredentials = has(credentialsByType, 'data_map') ? credentialsByType.data_map : null;

  sortedIds = dataMapCredentials ? difference(sortedIds, dataMapCredentials) : sortedIds;
  let byId = dataMapCredentials ? difference(result, dataMapCredentials) : result;

  if (has(credentialsByType, 'data_map')) {
    delete credentialsByType.data_map;
  }

  return {
    ...state,
    byId,
    byType: credentialsByType,
    sortedIds,
    lastUpdated: action.receivedAt,
    error: null,
  };
}

function saveCredential(state, action) {
  const {
    result,
    entities: { credentials, vendors },
  } = action;

  const thisCredential = credentials[result];
  let type = thisCredential.credentials_type;

  if (type === 'data_map') {
    return state;
  }

  let credentialsOfThisType = has(state.byType, type) ? state.byType[type] : [];
  // also check if this is a vendor based cred.

  let newByType = {
    ...state.byType,
  };

  const vendorName =
    has(thisCredential, 'vendor_id') &&
    !isNil(thisCredential.vendor_id) &&
    has(vendors, thisCredential.vendor_id)
      ? vendors[thisCredential.vendor_id].name
      : null;

  if (vendorName) {
    //if grouped creds doesnt contain vendor key
    let credsOfThisVendor = has(state.byType, vendorName) ? state.byType[vendorName] : [];

    newByType[vendorName] = [...new Set([result, ...credsOfThisVendor])];
  } else {
    newByType[type] = [...new Set([result, ...credentialsOfThisType])];
  }

  let sortedIds = includes(state.sortedIds, result)
    ? [...new Set([...state.sortedIds])]
    : [...new Set([result, ...state.sortedIds])];

  return {
    ...state,
    byId: [...new Set([...state.byId, result])],
    sortedIds: sortedIds,
    byType: newByType,
    lastUpdated: action.receivedAt,
    error: null,
  };
}

function saveCredentialListing(state, action) {
  const { result } = action;

  return {
    ...state,
    listing: result,
  };
}

function removeCredentials(state, action) {
  return {
    ...state,
    lastUpdated: action.receivedAt,
    byId: [],
    sortedIds: [],
    byType: {},
    error: action.error,
  };
}

function addCredential(state, action) {
  let {
    entities: { credentials, vendors },
    result,
  } = action;
  const thisCredential = credentials[result];

  let type = thisCredential.credentials_type;

  if (type === 'data_map') {
    return state;
  }

  let credentialsOfThisType = has(state.byType, type) ? state.byType[type] : [];

  let newByType = {
    ...state.byType,
  };

  const vendorName =
    has(thisCredential, 'vendor_id') &&
    !isNil(thisCredential.vendor_id) &&
    has(vendors, thisCredential.vendor_id)
      ? vendors[thisCredential.vendor_id].name
      : null;

  if (vendorName) {
    //if grouped creds doesnt contain vendor key
    let credsOfThisVendor = has(state.byType, vendorName) ? state.byType[vendorName] : [];

    newByType[vendorName] = [...credsOfThisVendor, thisCredential.id];
  } else {
    newByType[type] = [...credentialsOfThisType, result];
  }

  return {
    ...state,
    byId: [...new Set([...state.byId, result])],
    sortedIds: [...new Set([result, ...state.sortedIds])],
    byType: newByType,
    lastUpdated: action.receivedAt,
    error: null,
  };
}

function probeCredential(state, action) {
  let {
    requestInfo: { id },
  } = action;

  return {
    ...state,
    probingId: id,
  };
}

function updateCredentialStatus(state, action) {
  if (action.requestInfo && action.requestInfo.id) {
    let id = action.requestInfo.id;
    let status =
      action.type === PROBE_CREDENTIAL_SUCCESS || action.type === PROBE_CREDENTIAL_SILENT_SUCCESS;
    let probeStatus = {
      ...state.probeStatus,
      [id]: { status, verifiedAt: action.receivedAt },
    };
    return { ...state, probeStatus, probingId: null };
  }
  return state;
}

function deleteCredential(state, action) {
  const {
    requestInfo: { id: deletedId },
  } = action;
  const filterFunction = (id) => id !== deletedId;
  const newListing = filter(state.listing, filterFunction);
  const newById = filter(state.byId, filterFunction);
  const newSortedByIds = filter(state.sortedIds, filterFunction);
  const newByType = {};
  forEach(state.byType, (t, k) => {
    newByType[k] = filter(t, filterFunction);
  });

  return {
    ...state,
    listing: newListing,
    byId: newById,
    sortedIds: newSortedByIds,
    byType: newByType,
    error: null,
    lastUpdated: action.receivedAt,
  };
}

function getCredentialsByType(state, action) {
  const {
    entities: { credentials, vendors },
    result,
  } = action;

  let myCredentials = pick(credentials, result);
  let myGroupedCredentials = groupBy(myCredentials, 'credentials_type');
  let myGroupedCredentialIds = mapValues(myGroupedCredentials, function (s) {
    return map(s, (item) => item.id);
  });

  //now parse creds based on vendor ids
  forEach(myCredentials, (cred) => {
    const vendorName =
      has(cred, 'vendor_id') && !isNil(cred.vendor_id) && has(vendors, cred.vendor_id)
        ? vendors[cred.vendor_id].name
        : null;
    const credType = cred.credentials_type;
    if (vendorName && vendorName !== credType) {
      //if grouped creds doesnt contain vendor key
      let credsOfThisVendor = has(myGroupedCredentialIds, vendorName)
        ? myGroupedCredentialIds[vendorName]
        : [];
      credsOfThisVendor = [...credsOfThisVendor, cred.id];
      myGroupedCredentialIds[vendorName] = credsOfThisVendor;
      //now remove this cred from grouped creds.
      myGroupedCredentialIds[credType] = filter(myGroupedCredentialIds[credType], (c) => {
        return c !== cred.id;
      });
    }
  });

  return myGroupedCredentialIds;
}
